/**
 * DrawdownAckTab — Phase C (+ Phase I filter/column polish)
 * Lists drawdown investors who have at least one receipt recorded.
 * Allows: bulk-send acknowledgment emails, and filter by whether an
 * acknowledgment has already been sent (All / Acknowledgment Sent / Never Sent).
 */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  ClipboardCheck, Eye, Send, Loader2, AlertCircle, CheckCircle, X,
  RefreshCw, StopCircle, Mail, Trash2, RotateCcw, EyeOff
} from 'lucide-react';
import EmailPreviewModal from '../../components/email/EmailPreviewModal';
import TeamMemberPicker from '../../components/email/TeamMemberPicker';
import RecipientSearchBar, { filterByQuery } from '../../components/email/RecipientSearchBar';
import useWhatsAppSend from '../../hooks/useWhatsAppSend';
import WhatsAppSendOptions from '../../components/whatsapp/WhatsAppSendOptions';
import WhatsAppSendResultModal from '../../components/whatsapp/WhatsAppSendResultModal';
import WhatsAppSendHistory from '../../components/whatsapp/WhatsAppSendHistory';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pending:    { cls: 'bg-gray-100 text-gray-700',   label: 'Pending' },
  running:    { cls: 'bg-blue-100 text-blue-700',   label: 'Sending...' },
  completed:  { cls: 'bg-green-100 text-green-800', label: 'Completed' },
  aborted:    { cls: 'bg-yellow-100 text-yellow-800', label: 'Aborted' },
  failed:     { cls: 'bg-red-100 text-red-800',     label: 'Failed' },
};
const ACK_STATUS_META = {
  sent:    { cls: 'bg-green-100 text-green-700', label: 'Sent' },
  failed:  { cls: 'bg-red-100 text-red-700',     label: 'Failed' },
  skipped: { cls: 'bg-yellow-100 text-yellow-700', label: 'Skipped' },
  pending: { cls: 'bg-gray-100 text-gray-600',   label: 'Pending' },
  aborted: { cls: 'bg-yellow-100 text-yellow-700', label: 'Aborted' },
};

const DrawdownAckTab = ({ drawdownId, currentUser }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  // Phase I — filter by ack-email status based on each row's `last_ack_status`.
  // 'sent' == investor has received a successful ack email at least once.
  // 'never' == no ack email has ever succeeded (null/pending/failed/skipped/aborted).
  const [filter, setFilter] = useState('all'); // all | sent | never
  const [showHidden, setShowHidden] = useState(false);
  const [togglingRowId, setTogglingRowId] = useState(null);
  const [recipientSearch, setRecipientSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [bccSelf, setBccSelf] = useState(false);
  const [consolidateByAccount, setConsolidateByAccount] = useState(false);
  const [dateFormat, setDateFormat] = useState('dd mmmm yyyy');
  const [numberFormat, setNumberFormat] = useState('western');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeJob, setActiveJob] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [ccUserIds, setCcUserIds] = useState([]);
  const [bccUserIds, setBccUserIds] = useState([]);

  // -------- WhatsApp parallel send --------
  const wa = useWhatsAppSend(drawdownId, 'acknowledgments');

  useEffect(() => {
    fetchRows();
    fetchTemplates();
    // eslint-disable-next-line
  }, [drawdownId]);

  // poll active job
  useEffect(() => {
    if (!activeJob || activeJob.status === 'completed' || activeJob.status === 'aborted' || activeJob.status === 'failed') return;
    const t = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/drawdowns/${drawdownId}/emails/jobs/${activeJob.id}`);
        setActiveJob(data);
        if (data.status === 'completed' || data.status === 'aborted' || data.status === 'failed') fetchRows();
      } catch { /* ignore */ }
    }, 1500);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [activeJob?.id, activeJob?.status]);

  const fetchRows = async () => {
    setLoading(true);
    try {
      // Always ask the backend for every row (including hidden ones).
      // Hidden rows are filtered client-side by the `showHidden` toggle so
      // Admins can selectively hide/restore investors from the ack-email list
      // without affecting the underlying receipts or transactions.
      const { data } = await axios.get(`${API}/drawdowns/${drawdownId}/acknowledgments?include_hidden=true`);
      setRows(data.rows || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load acknowledgments');
    } finally { setLoading(false); }
  };

  const fetchTemplates = async () => {
    try {
      const { data } = await axios.get(`${API}/email-templates?scope=approved&section=acknowledgments`);
      // For ack emails, allow drawdown_investor + investor_account + general
      setTemplates((data || []).filter(t => ['drawdown_investor', 'investor_account', 'general'].includes(t.context_type)));
    } catch { /* ignore */ }
  };

  // Filter tab counts + the visible slice for the current tab.
  // Hidden rows (every receipt flagged hidden_from_ack) are excluded unless `showHidden`.
  const counts = useMemo(() => {
    let sent = 0, never = 0, hidden = 0;
    rows.forEach(r => {
      if (r.all_hidden) hidden += 1;
      if (r.all_hidden && !showHidden) return; // exclude from sent/never tally when hidden filter is off
      if (r.last_ack_status === 'sent') sent += 1;
      else never += 1;
    });
    const all = rows.filter(r => showHidden || !r.all_hidden).length;
    return { all, sent, never, hidden };
  }, [rows, showHidden]);

  const visibleRows = useMemo(() => {
    let base = rows;
    if (!showHidden) base = base.filter(r => !r.all_hidden);
    if (filter === 'sent') base = base.filter(r => r.last_ack_status === 'sent');
    else if (filter === 'never') base = base.filter(r => r.last_ack_status !== 'sent');
    return filterByQuery(base, recipientSearch, ['account_name', 'class_name', 'folio_no', 'pan']);
  }, [rows, filter, recipientSearch, showHidden]);

  const toggleRow = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleAll = () => {
    // Hidden rows are not eligible for sending — exclude them from Select All.
    const visibleIds = visibleRows.filter(r => !r.all_hidden).map(r => r.drawdown_investor_id);
    const allSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.includes(id));
    if (allSelected) {
      // De-select all rows currently in view, but keep any selections outside this filter.
      setSelectedIds(prev => prev.filter(id => !visibleIds.includes(id)));
    } else {
      setSelectedIds(prev => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const handleHideToggle = async (row) => {
    const isHidden = !!row.all_hidden;
    if (!isHidden) {
      const ok = window.confirm(
        `Remove "${row.account_name}" from the acknowledgment email list?\n\n` +
        `Underlying receipts and transactions stay intact — only the email send list is affected. ` +
        `Toggle "Show hidden" to restore later.`
      );
      if (!ok) return;
    }
    setTogglingRowId(row.drawdown_investor_id);
    setError(''); setSuccess('');
    try {
      const path = isHidden ? 'unhide' : 'hide';
      await axios.post(`${API}/drawdowns/${drawdownId}/acknowledgments/${row.drawdown_investor_id}/${path}`);
      // Drop from selection (hidden rows shouldn't stay selected for send)
      setSelectedIds(prev => prev.filter(id => id !== row.drawdown_investor_id));
      setSuccess(isHidden ? `Restored "${row.account_name}" to the list.` : `Removed "${row.account_name}" from the list.`);
      await fetchRows();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update row');
    } finally {
      setTogglingRowId(null);
    }
  };

  const [sendEmail, setSendEmail] = useState(true);

  const handleSend = async () => {
    setError(''); setSuccess('');
    if (!selectedTemplate) { setError('Pick an approved template first.'); return; }
    if (selectedIds.length === 0) { setError('Select at least one row.'); return; }
    if (!sendEmail && !wa.canSend) {
      setError('Nothing to send — enable the email checkbox or the "Also send WhatsApp" toggle.');
      return;
    }
    try {
      let emailQueued = 0;
      if (sendEmail) {
        const { data } = await axios.post(`${API}/drawdowns/${drawdownId}/acknowledgments/send`, {
          template_id: selectedTemplate,
          drawdown_investor_ids: selectedIds,
          bcc_self: bccSelf,
          date_format: dateFormat,
          number_format: numberFormat,
          cc_user_ids: ccUserIds,
          bcc_user_ids: bccUserIds,
          consolidate_by_account: consolidateByAccount,
        });
        const { data: job } = await axios.get(`${API}/drawdowns/${drawdownId}/emails/jobs/${data.job_id}`);
        setActiveJob(job);
        emailQueued = data.total;
      }

      // Optional parallel WhatsApp acknowledgment send — independent template.
      if (wa.canSend) {
        const res = await wa.triggerSend({
          drawdownInvestorIds: selectedIds,
          preferProtected: true,
          dateFormat,
          numberFormat,
        });
        if (!res.ok) {
          setError(sendEmail
            ? `Acknowledgments queued OK, but WhatsApp send failed: ${res.error}`
            : `WhatsApp acknowledgment failed: ${res.error}`);
          return;
        }
      }
      setSelectedIds([]);
      if (sendEmail && wa.canSend) {
        setSuccess(`Queued ${emailQueued} acknowledgment email(s) + a parallel WhatsApp send.`);
      } else if (sendEmail) {
        setSuccess(`Queued ${emailQueued} acknowledgment email(s).`);
      } else {
        setSuccess('WhatsApp acknowledgment queued — see the progress modal for live status.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send');
    }
  };

  const handleAbort = async () => {
    if (!activeJob) return;
    if (!window.confirm('Abort sending? Already-sent emails cannot be unsent.')) return;
    try {
      await axios.post(`${API}/drawdowns/${drawdownId}/emails/jobs/${activeJob.id}/abort`);
      setSuccess('Abort requested...');
    } catch (err) { setError(err.response?.data?.detail || 'Failed to abort'); }
  };

  const senderEnabled = !!currentUser?.email_sending_enabled;
  const sendingActive = activeJob && (activeJob.status === 'pending' || activeJob.status === 'running');
  const progressPct = activeJob && activeJob.total > 0
    ? Math.round(((activeJob.sent || 0) + (activeJob.failed || 0) + (activeJob.skipped || 0)) / activeJob.total * 100)
    : 0;
  const allSelectedOnPage = (() => {
    const eligibleIds = visibleRows.filter(r => !r.all_hidden).map(r => r.drawdown_investor_id);
    return eligibleIds.length > 0 && eligibleIds.every(id => selectedIds.includes(id));
  })();

  return (
    <div className="space-y-6" data-testid="dd-ack-tab">
      {!senderEnabled && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start" data-testid="dd-ack-disabled-notice">
          <AlertCircle className="text-yellow-600 mr-2 flex-shrink-0 mt-0.5" size={20} />
          <div className="text-sm text-yellow-900">
            <p className="font-semibold">Email sending is disabled for your account.</p>
            <p>To send acknowledgment emails, ask an Admin to enable sending and configure your <Link className="underline text-blue-700" to="/email-config">SMTP credentials</Link>.</p>
          </div>
        </div>
      )}

      {/* Bulk action bar */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs font-medium text-gray-600 mb-1">Approved Template *</label>
            <select
              value={selectedTemplate}
              onChange={e => setSelectedTemplate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              data-testid="dd-ack-template-select"
            >
              <option value="">— Pick a template —</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <label className="flex items-start cursor-pointer text-sm text-gray-700 mb-2" data-testid="dd-ack-send-email-toggle">
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={e => setSendEmail(e.target.checked)}
              className="h-4 w-4 text-blue-600 rounded mt-0.5"
            />
            <span className="ml-2">
              <Mail size={12} className="inline mr-1 text-blue-600" />
              Send email acknowledgment
              <span className="text-gray-500"> — uncheck for a WhatsApp-only resend.</span>
            </span>
          </label>
          <label className="flex items-center cursor-pointer text-sm mb-2">
            <input type="checkbox" checked={bccSelf} onChange={e => setBccSelf(e.target.checked)} disabled={!sendEmail} className={`h-4 w-4 text-blue-600 rounded ${!sendEmail ? 'opacity-40 cursor-not-allowed' : ''}`} data-testid="dd-ack-bcc-self" />
            <span className={`ml-2 ${sendEmail ? 'text-gray-700' : 'text-gray-400'}`}>BCC me</span>
          </label>
          {rows.some(r => r.class_name) && (
            <label className="flex items-start cursor-pointer text-sm text-gray-700 mb-2">
              <input type="checkbox" checked={consolidateByAccount} onChange={e => setConsolidateByAccount(e.target.checked)} className="h-4 w-4 text-blue-600 rounded mt-0.5" data-testid="dd-ack-consolidate-account" />
              <span className="ml-2">Single email per account<span className="text-gray-500"> — combines every class for the same investor.</span></span>
            </label>
          )}
          <div className="min-w-[180px]">
            <label className="block text-xs font-medium text-gray-600 mb-1">Date Format</label>
            <select value={dateFormat} onChange={e => setDateFormat(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              data-testid="dd-ack-date-format-select">
              <option value="dd mmmm yyyy">25 January 2026</option>
              <option value="dd-mm-yyyy">25-01-2026</option>
              <option value="mm-dd-yyyy">01-25-2026</option>
              <option value="dd/mm/yyyy">25/01/2026</option>
              <option value="mm/dd/yyyy">01/25/2026</option>
              <option value="yyyy-mm-dd">2026-01-25</option>
              <option value="mmmm dd, yyyy">January 25, 2026</option>
              <option value="dd-mmmm-yyyy">25-January-2026</option>
            </select>
          </div>
          <div className="min-w-[170px]">
            <label className="block text-xs font-medium text-gray-600 mb-1">Number Format</label>
            <select value={numberFormat} onChange={e => setNumberFormat(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              data-testid="dd-ack-number-format-select">
              <option value="western">Western (1,000,000)</option>
              <option value="indian">Indian (10,00,000)</option>
            </select>
          </div>
          <button
            onClick={() => setPreviewOpen(true)}
            type="button"
            disabled={!selectedTemplate || selectedIds.length === 0}
            className="px-4 py-2 bg-white border border-gray-300 text-gray-800 rounded-lg hover:bg-gray-50 text-sm font-semibold flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="dd-ack-preview-btn"
            title="Preview the acknowledgment for one selected investor"
          >
            <Eye size={14} className="mr-2" /> Preview
          </button>
          <button
            onClick={handleSend}
            disabled={!senderEnabled || sendingActive || selectedIds.length === 0 || !selectedTemplate}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-semibold flex items-center disabled:opacity-50"
            data-testid="dd-ack-send-btn"
          >
            <Send size={14} className="mr-2" /> Send Acknowledgment to {selectedIds.length || 0}
          </button>
        </div>

        {/* Team Member Cc / Bcc */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <TeamMemberPicker
            label="CC team members"
            value={ccUserIds}
            onChange={setCcUserIds}
            placeholder="Add teammates to Cc on every acknowledgment…"
            testid="dd-ack-cc-team"
          />
          <TeamMemberPicker
            label="BCC team members"
            value={bccUserIds}
            onChange={setBccUserIds}
            placeholder="Add teammates to Bcc on every acknowledgment…"
            testid="dd-ack-bcc-team"
          />
        </div>

        {/* WhatsApp parallel acknowledgment send */}
        <div className="mt-3 border-t border-gray-100 pt-3">
          <WhatsAppSendOptions
            wa={wa}
            label="fires a parallel WhatsApp acknowledgment over the same selected recipients."
          />
        </div>
        {error && (
          <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800 flex items-center" data-testid="dd-ack-error">
            <AlertCircle size={12} className="text-red-600 mr-2" /> {error}
          </div>
        )}
        {success && (
          <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-800 flex items-center" data-testid="dd-ack-success">
            <CheckCircle size={12} className="text-green-600 mr-2" /> {success}
          </div>
        )}
      </div>

      <EmailPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        drawdownId={drawdownId}
        templateId={selectedTemplate}
        kind="drawdown_acknowledgment"
        recipients={rows
          .filter(r => selectedIds.includes(r.drawdown_investor_id))
          .map(r => ({ id: r.drawdown_investor_id, account_name: r.account_name, class_name: r.class_name, folio_no: r.folio_no }))}
        includeDrawdownNotice={false}
        bccSelf={bccSelf}
        dateFormat={dateFormat}
        numberFormat={numberFormat}
        ccUserIds={ccUserIds}
        bccUserIds={bccUserIds}
      />

      {/* WhatsApp send result modal */}
      {wa.resultOpen && wa.activeJob && (
        <WhatsAppSendResultModal
          job={wa.activeJob}
          onClose={() => wa.setResultOpen(false)}
        />
      )}

      <WhatsAppSendHistory
        drawdownId={drawdownId}
        refreshKey={(wa.activeJob && ['completed','failed','aborted'].includes(wa.activeJob.status)) ? wa.activeJob.id : 0}
      />

      {/* Active job */}
      {activeJob && (
        <div className="bg-white border border-blue-300 rounded-lg p-4" data-testid="dd-ack-active-job">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center">
              <Loader2 size={14} className={`mr-2 ${sendingActive ? 'animate-spin text-blue-600' : 'text-gray-400'}`} />
              {sendingActive ? 'Sending acknowledgments...' : 'Last send result'}
            </h3>
            <div className="flex items-center gap-2 text-xs">
              <span className={`px-2 py-0.5 rounded-full font-medium ${(STATUS_META[activeJob.status] || STATUS_META.pending).cls}`}>{(STATUS_META[activeJob.status] || STATUS_META.pending).label}</span>
              {sendingActive && (
                <button onClick={handleAbort} className="flex items-center bg-red-50 text-red-700 px-2 py-1 rounded hover:bg-red-100" data-testid="dd-ack-abort-btn">
                  <StopCircle size={12} className="mr-1" /> Abort
                </button>
              )}
              <button onClick={() => setActiveJob(null)} className="text-gray-400 hover:text-gray-700"><X size={12} /></button>
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 mb-2"><div className="bg-blue-500 h-2 rounded-full transition-all" style={{ width: `${progressPct}%` }} /></div>
          <p className="text-xs text-gray-600 text-center">{activeJob.sent || 0} sent · {activeJob.failed || 0} failed · {activeJob.skipped || 0} skipped · of {activeJob.total} total</p>
        </div>
      )}

      {/* Ack rows table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center gap-3">
          <ClipboardCheck size={16} className="text-gray-600" />
          <h3 className="text-sm font-bold text-gray-900">Drawdown Acknowledgments</h3>
          <span className="text-xs text-gray-500">{visibleRows.length} row{visibleRows.length === 1 ? '' : 's'}</span>
          <button onClick={fetchRows} className="ml-auto text-xs text-gray-500 hover:text-gray-800 flex items-center" title="Refresh" data-testid="dd-ack-list-refresh">
            <RefreshCw size={12} className="mr-1" /> Refresh
          </button>
        </div>

        {/* Phase I — filter tabs based on Last Ack Email status */}
        <div className="px-4 pt-3 border-b border-gray-200">
          <div className="flex items-end gap-1 flex-wrap" role="tablist" data-testid="dd-ack-filter-tabs">
            {[
              { id: 'all',   label: 'All',                 count: counts.all },
              { id: 'sent',  label: 'Acknowledgment Sent', count: counts.sent },
              { id: 'never', label: 'Never Sent',          count: counts.never },
            ].map(t => (
              <button
                key={t.id}
                type="button"
                onClick={() => { setFilter(t.id); }}
                role="tab"
                aria-selected={filter === t.id}
                className={`px-3 py-1.5 text-sm border-b-2 -mb-px transition-colors ${
                  filter === t.id ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
                data-testid={`dd-ack-filter-${t.id}`}
              >
                {t.label} <span className="ml-1 text-xs text-gray-400">({t.count})</span>
              </button>
            ))}
            <div className="ml-auto pb-2 flex flex-wrap items-center gap-3">
              <label className="flex items-center cursor-pointer text-xs text-gray-700 select-none" data-testid="dd-ack-show-hidden-wrapper">
                <input
                  type="checkbox"
                  checked={showHidden}
                  onChange={e => setShowHidden(e.target.checked)}
                  className="h-3.5 w-3.5 text-blue-600 rounded mr-1.5"
                  data-testid="dd-ack-show-hidden-toggle"
                />
                <span className="flex items-center"><EyeOff size={12} className="mr-1 text-gray-500" />Show hidden ({counts.hidden})</span>
              </label>
              <div className="w-full sm:w-72">
                <RecipientSearchBar
                  value={recipientSearch}
                  onChange={setRecipientSearch}
                  placeholder="Search investor, class, folio, PAN…"
                  testid="dd-ack-recipient-search"
                />
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-500"><Loader2 className="animate-spin mx-auto" size={20} /></div>
        ) : visibleRows.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500" data-testid="dd-ack-empty">
            <Mail className="mx-auto text-gray-300 mb-2" size={32} />
            {recipientSearch
              ? `No investors match "${recipientSearch}".`
              : filter === 'sent'
              ? 'No acknowledgment emails have been sent yet on this drawdown.'
              : filter === 'never'
              ? 'Every investor with receipts has already received an acknowledgment.'
              : 'No receipts yet on this drawdown. Once investors record receipts in the Investors tab, they will appear here for acknowledgment.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2 w-8 text-left">
                    <input
                      type="checkbox"
                      checked={allSelectedOnPage}
                      onChange={toggleAll}
                      data-testid="dd-ack-select-all"
                      className="h-4 w-4 text-blue-600 rounded"
                    />
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Investor</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Class / Folio</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Drawdown</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Total Received</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase"># Receipts</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Last Receipt</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Last Ack Email</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visibleRows.map(row => {
                  const ackMeta = row.last_ack_status ? (ACK_STATUS_META[row.last_ack_status] || ACK_STATUS_META.pending) : null;
                  const isHidden = !!row.all_hidden;
                  const isToggling = togglingRowId === row.drawdown_investor_id;
                  const isAdmin = currentUser?.role === 'admin';
                  return (
                    <tr key={row.drawdown_investor_id} className={`hover:bg-gray-50 ${isHidden ? 'bg-gray-50 opacity-70' : ''}`} data-testid={`dd-ack-row-${row.drawdown_investor_id}`}>
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(row.drawdown_investor_id)}
                          onChange={() => toggleRow(row.drawdown_investor_id)}
                          disabled={isHidden}
                          title={isHidden ? 'Restore this row before selecting for send' : ''}
                          data-testid={`dd-ack-pick-${row.drawdown_investor_id}`}
                          className="h-4 w-4 text-blue-600 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{row.account_name}</span>
                          {isHidden && (
                            <span className="px-1.5 py-0.5 text-[10px] uppercase tracking-wide font-semibold bg-gray-200 text-gray-700 rounded" data-testid={`dd-ack-hidden-badge-${row.drawdown_investor_id}`}>
                              Hidden
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 truncate max-w-xs">
                          {(row.to_emails || []).length > 0 ? (
                            <div><span className="font-semibold">To:</span> {(row.to_emails || []).join(', ')}</div>
                          ) : <em>no primary email</em>}
                          {(row.cc_emails || []).length > 0 && (
                            <div><span className="font-semibold">Cc:</span> {(row.cc_emails || []).join(', ')}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">
                        {row.class_name && <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded mr-1">{row.class_name}</span>}
                        <span>{row.folio_no || '—'}</span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{(row.drawdown_amount || 0).toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs text-green-700">{(row.total_received_visible || 0).toLocaleString()}</td>
                      <td className="px-3 py-2 text-right text-xs">
                        {row.receipt_count_visible}
                        {row.receipt_count_hidden > 0 && (
                          <span className="ml-1 text-gray-400">+{row.receipt_count_hidden} hidden</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">{row.last_receipt_date || '—'}</td>
                      <td className="px-3 py-2 text-xs">
                        {ackMeta ? (
                          <div className="flex flex-col">
                            <span className={`px-2 py-0.5 rounded-full font-medium w-fit ${ackMeta.cls}`} data-testid={`dd-ack-lastack-${row.drawdown_investor_id}`}>{ackMeta.label}</span>
                            {row.last_ack_sent_at && <span className="text-[10px] text-gray-500 mt-0.5">{new Date(row.last_ack_sent_at).toLocaleString()}</span>}
                          </div>
                        ) : <span className="text-gray-400 italic" data-testid={`dd-ack-lastack-${row.drawdown_investor_id}`}>never sent</span>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {isAdmin ? (
                          isHidden ? (
                            <button
                              onClick={() => handleHideToggle(row)}
                              disabled={isToggling}
                              className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded border border-green-200 disabled:opacity-50"
                              data-testid={`dd-ack-restore-${row.drawdown_investor_id}`}
                              title="Restore this investor to the acknowledgment email list"
                            >
                              {isToggling ? <Loader2 size={12} className="animate-spin mr-1" /> : <RotateCcw size={12} className="mr-1" />}
                              Restore
                            </button>
                          ) : (
                            <button
                              onClick={() => handleHideToggle(row)}
                              disabled={isToggling}
                              className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded border border-red-200 disabled:opacity-50"
                              data-testid={`dd-ack-hide-${row.drawdown_investor_id}`}
                              title="Remove this investor from the acknowledgment email list (receipts unaffected)"
                            >
                              {isToggling ? <Loader2 size={12} className="animate-spin mr-1" /> : <Trash2 size={12} className="mr-1" />}
                              Delete
                            </button>
                          )
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DrawdownAckTab;
